// CogsVisuals.tsx — shared COGS presentation primitives so the Bundle Categories cards, the
// parent detail / Version History view and the Simple/Bundle table all render COGS with one
// implementation: the same brand tints, the same count-up and the same stacked composition bar.
//
// Nothing here changes the COGS data or its shape — these are purely visual. All colour comes
// from the warm Salty palette (tokens.ts); there is no raw/random hex for the segments. The
// segment tints are an ORDERED array derived from the brand orange → ink so adjacent children in
// the stacked bar stay distinguishable while keeping the brand identity.

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { tokens } from '../../theme/tokens';
import { formatInr } from '../../utils/cogsFormat';

// Ordered, warm tint ramp for the cost-composition segments. Derived from the brand orange
// (tokens.orange / orangeLight / orangeDark) stepping toward ink — no unrelated hues. Adjacent
// entries differ enough in lightness to read as distinct bar segments; the ramp cycles for
// bundles with more children than entries.
export const COGS_SEGMENT_TINTS: readonly string[] = [
    tokens.orange,       // #DC5829 — the brand accent leads
    '#E8824F',           // lighter warm orange
    tokens.orangeDark,   // #B8451E — deeper burnt orange
    '#F0A878',           // soft terracotta
    '#8A3516',           // dark ember
    '#C96A3C',           // mid clay
    '#5C3220',           // warm brown toward ink
    '#EFC3A3',           // pale sand
] as const;

export const cogsSegmentColor = (index: number): string =>
    COGS_SEGMENT_TINTS[index % COGS_SEGMENT_TINTS.length];

// ── AnimatedInr ─────────────────────────────────────────────────────────────
// Count-up of an INR figure: tweens 0 → value on mount (~0.5s, ease-out), rendered with the
// shared formatInr so the final frame matches every other COGS figure exactly. Honors reduced
// motion — when the user prefers reduced motion the final value is shown instantly, no tween.
export const AnimatedInr: React.FC<{
    value: number | null | undefined;
    duration?: number;
    sx?: object;
    className?: string;
}> = ({ value, duration = 0.5, sx, className }) => {
    const reduceMotion = useReducedMotion();
    const [display, setDisplay] = React.useState<number>(
        reduceMotion || value === null || value === undefined ? (value ?? 0) : 0,
    );

    React.useEffect(() => {
        if (value === null || value === undefined) {
            setDisplay(value ?? 0);
            return;
        }
        if (reduceMotion) {
            setDisplay(value);
            return;
        }
        let raf = 0;
        const start = performance.now();
        const from = 0;
        const to = value;
        const ms = duration * 1000;
        const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
        const tick = (now: number) => {
            const t = Math.min(1, (now - start) / ms);
            setDisplay(from + (to - from) * easeOut(t));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [value, duration, reduceMotion]);

    if (value === null || value === undefined) {
        return (
            <Typography component="span" className={className} sx={{ fontVariantNumeric: 'tabular-nums', ...sx }}>
                {formatInr(value)}
            </Typography>
        );
    }
    return (
        <Typography component="span" className={className} sx={{ fontVariantNumeric: 'tabular-nums', ...sx }}>
            {formatInr(display)}
        </Typography>
    );
};

// ── CogsShareBar ──────────────────────────────────────────────────────────────
// A single child's share-of-costed-total mini bar: a thin track with a brand-tinted fill that
// grows from 0 → pct on mount, plus the % label. Used in the detail legend and the table's
// per-child % column so both read identically. Reduced motion → static final width.
export const CogsShareBar: React.FC<{
    pct: number;
    color?: string;
    width?: number;
    height?: number;
    showLabel?: boolean;
    delay?: number;
}> = ({ pct, color = tokens.orange, width = 44, height = 6, showLabel = true, delay = 0 }) => {
    const reduceMotion = useReducedMotion();
    const target = Math.min(100, Math.max(0, pct));
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, minWidth: showLabel ? 96 : width }}>
            <Box sx={{ position: 'relative', width, height, borderRadius: 3, bgcolor: 'rgba(26,20,15,0.08)', overflow: 'hidden', flexShrink: 0 }}>
                <motion.span
                    initial={reduceMotion ? false : { width: 0 }}
                    animate={{ width: `${target}%` }}
                    transition={{ duration: 0.45, delay: reduceMotion ? 0 : delay, ease: [0.16, 1, 0.3, 1] }}
                    style={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 3, background: color }}
                />
            </Box>
            {showLabel && (
                <Typography component="span" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '0.78rem' }}>
                    {target.toFixed(1)}%
                </Typography>
            )}
        </Box>
    );
};

// ── CogsCompositionBar ──────────────────────────────────────────────────────────
// The cost-composition visual for a version: one 100% stacked bar where each COSTED child is a
// segment whose width is its share (cogsPct) of the costed total, animated growing from 0 on
// mount (staggered), followed by a legend grid. Uncosted children and packaging `extra` children
// are listed in the legend (muted) but EXCLUDED from the bar — same rule as the % helper.
export type CogsCompositionItem = {
    sku: string;
    cogs?: number | null;   // undefined = not enriched, null = missing cost, number = costed
    cogsPct?: number | null; // share of the costed total; null/undefined for packaging/missing
    extra?: boolean;         // packaging attribute — excluded from the bar/total
};

export const CogsCompositionBar: React.FC<{ items: CogsCompositionItem[] }> = ({ items }) => {
    const reduceMotion = useReducedMotion();

    // Segments = costed, non-packaging children with a positive share. Index into the tint ramp
    // is stable across the segment order so the legend swatch matches its bar segment.
    const segments = items
        .filter((it) => !it.extra && typeof it.cogsPct === 'number' && (it.cogsPct as number) > 0)
        .map((it, i) => ({ ...it, color: cogsSegmentColor(i), pct: Math.max(0, it.cogsPct as number) }));

    const hasBar = segments.length > 0;

    return (
        <Box>
            {hasBar && (
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        height: 12,
                        borderRadius: 999,
                        overflow: 'hidden',
                        bgcolor: 'rgba(26,20,15,0.06)',
                        mb: 1,
                    }}
                >
                    {segments.map((seg, i) => (
                        <Tooltip key={`${seg.sku}-${i}`} title={`${seg.sku} · ${formatInr(seg.cogs)} · ${seg.pct.toFixed(1)}%`}>
                            <motion.div
                                initial={reduceMotion ? false : { width: 0 }}
                                animate={{ width: `${Math.min(100, seg.pct)}%` }}
                                transition={{ duration: 0.5, delay: reduceMotion ? 0 : Math.min(i, 10) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                                style={{
                                    height: '100%',
                                    background: seg.color,
                                    borderRight: i < segments.length - 1 ? '1px solid rgba(255,255,255,0.6)' : 'none',
                                    minWidth: 2,
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
            )}

            {/* Legend — a clean vertical list with three ALIGNED columns: COMPONENT (swatch +
                SKU, left), COST (right-aligned) and SHARE (right-aligned). Every row shares the
                same grid template so the figures line up in fixed columns instead of the old
                auto-fill wrapping that ran the entries together. Costed children carry their
                segment colour; missing/packaging children read muted and distinct. */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto 72px',
                    columnGap: 1.5,
                    rowGap: 0.4,
                    alignItems: 'center',
                }}
            >
                {/* Column headers */}
                <Typography component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: tokens.muted }}>
                    Component
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: tokens.muted, textAlign: 'right' }}>
                    Cost
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: tokens.muted, textAlign: 'right' }}>
                    Share
                </Typography>

                {items.map((it, i) => {
                    const seg = segments.find((s) => s.sku === it.sku);
                    const isSegment = !!seg;
                    const swatch = seg ? seg.color : 'rgba(26,20,15,0.14)';
                    const missing = it.cogs === null;
                    return (
                        <React.Fragment key={`${it.sku}-legend-${i}`}>
                            {/* COMPONENT */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: swatch, flexShrink: 0, ...(isSegment ? {} : { border: '1px solid rgba(26,20,15,0.18)' }) }} />
                                <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.7rem', color: tokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {it.sku}
                                </Typography>
                            </Box>

                            {/* COST */}
                            {missing ? (
                                <Tooltip title="No COGS found for this SKU">
                                    <Typography component="span" sx={{ color: '#b45309', fontWeight: 700, fontSize: '0.66rem', cursor: 'help', textAlign: 'right' }}>no cost</Typography>
                                </Tooltip>
                            ) : it.extra ? (
                                <Tooltip title="Packaging attribute of the bundle — not part of the bundle COGS">
                                    <Typography component="span" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontSize: '0.68rem', color: tokens.muted, cursor: 'help', textAlign: 'right' }}>
                                        {formatInr(it.cogs)} · pkg
                                    </Typography>
                                </Tooltip>
                            ) : (
                                <Typography component="span" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontSize: '0.68rem', color: tokens.ink, textAlign: 'right' }}>
                                    {formatInr(it.cogs)}
                                </Typography>
                            )}

                            {/* SHARE */}
                            <Typography component="span" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontSize: '0.68rem', color: tokens.muted, textAlign: 'right' }}>
                                {!missing && !it.extra && typeof it.cogsPct === 'number' ? `${it.cogsPct.toFixed(1)}%` : '—'}
                            </Typography>
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
};
